import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import i2Math from '@/utils/i2Math';
import { renderPhoneStr, renderTextStr } from '@/utils/renderUtils';
import { fetchStoreRoomStatus } from '@/redux/dashboard/getStoreRoomStatus/storeRoomStatusSlice';
import { fetchCommandCenterOccupiedRooms } from '@/redux/dashboard/commandCenterOccupiedRooms/commandCenterOccupiedRoomsSlice';
import { fetchCommandCenterStaffAgentsPage } from '@/redux/dashboard/commandCenterStaffAgentsPage/commandCenterStaffAgentsPageSlice';
import { fetchStaffList } from '@/redux/dashboard/staffList/StaffSlice';

/** 与指挥中心 RoomsManagementModule 同一份门店物理房间总数 */
const STORE_ROOM_TOTAL_CAP = {
  16: 79,
  86: 34
};

function RoomStaffStats({ storeId }) {
  const dispatch = useDispatch();
  const storeRoomStatus = useSelector((state) => state.dashboard?.storeRoomStatus);
  const commandCenterOccupiedRooms = useSelector((state) => state.dashboard?.commandCenterOccupiedRooms);
  const commandCenterStaffAgentsPage = useSelector((state) => state.dashboard?.commandCenterStaffAgentsPage);
  const staffList = useSelector((state) => state.dashboard?.staffList);
  const [openPanel, setOpenPanel] = useState('');
  const [roomPage, setRoomPage] = useState(0);
  const [staffPage, setStaffPage] = useState(0);

  const selectedStoreId = storeId != null && String(storeId).trim() !== '' ? String(storeId).trim() : '';
  const today = dayjs().format('YYYY-MM-DD');

  useEffect(() => {
    if (!selectedStoreId) return undefined;
    const pull = () => {
      dispatch(fetchStoreRoomStatus({
        query: { store_id: selectedStoreId, date: today }
      })).unwrap().catch(() => {});
      dispatch(fetchCommandCenterOccupiedRooms({
        query: {
          storeId: selectedStoreId,
          checkInStatus: 2,
          keyword: '',
          pageNumber: 1,
          pageSize: 100
        }
      })).unwrap().catch(() => {});
    };
    pull();
    const timer = setInterval(pull, 30000);
    return () => clearInterval(timer);
  }, [dispatch, selectedStoreId, today]);

  useEffect(() => {
    if (!selectedStoreId) return undefined;
    dispatch(fetchCommandCenterStaffAgentsPage({
      query: {
        storeId: selectedStoreId,
        state: '1',
        pageNumber: 1,
        pageSize: 100
      }
    })).unwrap().catch(() => {});
    dispatch(fetchStaffList({
      query: { store_id: selectedStoreId }
    })).unwrap().catch(() => {});
    return undefined;
  }, [dispatch, selectedStoreId]);

  const roomStatusSameStore = String(storeRoomStatus?.query?.store_id ?? '') === selectedStoreId;
  const roomStatusData = roomStatusSameStore ? (storeRoomStatus?.data || {}) : {};
  const occupiedSameStore =
    String(commandCenterOccupiedRooms?.query?.storeId ?? '') === selectedStoreId &&
    commandCenterOccupiedRooms?.error == null;
  const occupiedTotalRow = commandCenterOccupiedRooms?.data?.totalRow;
  const hasOrderTotal =
    occupiedSameStore &&
    occupiedTotalRow !== undefined &&
    occupiedTotalRow !== null &&
    !Number.isNaN(Number(occupiedTotalRow));
  const occupied = hasOrderTotal
    ? (Number(occupiedTotalRow) || 0)
    : (Number(roomStatusData.occupiedRooms) || 0);
  const cap = Number(STORE_ROOM_TOTAL_CAP[selectedStoreId]) || 0;
  const apiTotalRooms = Number(roomStatusData.totalRooms) || 0;
  const roomTotal = cap > 0 ? cap : apiTotalRooms;
  const maintenanceRooms = Number(roomStatusData.maintenanceRooms) || 0;
  const availableRooms = i2Math.i2Sub(roomTotal, maintenanceRooms);
  const roomRate = availableRooms > 0
    ? Math.round(i2Math.i2Multiply(i2Math.i2Division(occupied, availableRooms), 100))
    : 0;

  const staffSameStore =
    String(commandCenterStaffAgentsPage?.query?.storeId ?? '') === selectedStoreId &&
    commandCenterStaffAgentsPage?.error == null;
  const staffTotalRow = commandCenterStaffAgentsPage?.data?.totalRow;
  const staffOk =
    staffSameStore &&
    staffTotalRow !== undefined &&
    staffTotalRow !== null &&
    !Number.isNaN(Number(staffTotalRow));
  const staffTotal = staffOk ? (Number(staffTotalRow) || 0) : 0;
  const occupiedRecords = occupiedSameStore && Array.isArray(commandCenterOccupiedRooms?.data?.records)
    ? commandCenterOccupiedRooms.data.records
    : [];
  const agentRecords = staffSameStore && Array.isArray(commandCenterStaffAgentsPage?.data?.records)
    ? commandCenterStaffAgentsPage.data.records
    : [];
  const staffPayload = staffList?.data;
  const nannyRows = Array.isArray(staffPayload?.data)
    ? staffPayload.data
    : (Array.isArray(staffPayload) ? staffPayload : []);
  const nannyReady = String(staffList?.query?.store_id ?? '') === selectedStoreId
    && staffList?.fetching !== true
    && staffList?.error == null;
  let nannyCount = null;
  if (nannyReady) {
    const hasPosition = nannyRows.some((row) => row && row.position_name);
    if (hasPosition) {
      nannyCount = nannyRows.filter((row) => /育婴|月嫂|育儿/.test(String(row.position_name || ''))).length;
    } else {
      nannyCount = nannyRows.length;
    }
  }

  return (
    <>
    <div className="room-staff-stats">
      <div className="rs-section rs-room" onClick={() => { setRoomPage(0); setOpenPanel('room'); }}>
        <div className="rs-header">
          <span className="rs-title">开房数</span>
        </div>
        <div className="rs-body">
          <div className="rs-ring-container">
            <svg className="rs-ring" viewBox="0 0 120 120">
              <circle
                className="rs-ring-bg"
                cx="60" cy="60" r="50"
              />
              <circle
                className="rs-ring-fill"
                cx="60" cy="60" r="50"
                strokeDasharray={`${2 * Math.PI * 50}`}
                strokeDashoffset={`${2 * Math.PI * 50 * (1 - roomRate / 100)}`}
              />
            </svg>
            <div className="rs-ring-center">
              <span className="rs-ring-value">{occupied}</span>
              <span className="rs-ring-label">房</span>
            </div>
          </div>
          <div className="rs-info">
            <div className="rs-info-row">
              <span className="rs-info-label">房间总数</span>
              <span className="rs-info-value">{roomTotal}</span>
            </div>
            <div className="rs-info-row">
              <span className="rs-info-label">报损</span>
              <span className="rs-info-value rs-idle">{maintenanceRooms}</span>
            </div>
          </div>
          <div className="rs-rate">
            <span className="rs-rate-value">{roomRate}%</span>
            <span className="rs-rate-label">开房率</span>
          </div>
        </div>
      </div>

      <div className="rs-section rs-staff" onClick={() => { setStaffPage(0); setOpenPanel('staff'); }}>
        <div className="rs-header">
          <span className="rs-title">员工总数</span>
        </div>
        <div className="rs-body">
          <div className="rs-ring-container">
            <svg className="rs-ring rs-ring-purple" viewBox="0 0 120 120">
              <circle
                className="rs-ring-bg"
                cx="60" cy="60" r="50"
              />
              <circle
                className="rs-ring-fill"
                cx="60" cy="60" r="50"
                strokeDasharray={`${2 * Math.PI * 50}`}
                strokeDashoffset={`${2 * Math.PI * 50}`}
              />
            </svg>
            <div className="rs-ring-center">
              <span className="rs-ring-value">{staffTotal}</span>
              <span className="rs-ring-label">人</span>
            </div>
          </div>
          <div className="rs-info">
            <div className="rs-info-row">
              <span className="rs-info-label">在职</span>
              <span className="rs-info-value rs-on">{staffTotal}</span>
            </div>
            <div className="rs-info-divider"></div>
            <div className="rs-nanny-section">
              <div className="rs-info-row rs-info-row-small">
                <span className="rs-info-label">育婴师/月嫂</span>
                <span className="rs-info-value rs-nanny-on">{nannyCount == null ? '--' : nannyCount}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
      {openPanel === 'room' && createPortal(
        <div className="rss-overlay rss-overlay-cyan" onClick={() => setOpenPanel('')}>
          <div className="rss-modal rss-modal-cyan" onClick={(e) => e.stopPropagation()}>
            <div className="rss-header rss-header-cyan">
              <div className="rss-header-left">
                <span className="rss-title rss-title-cyan">房间情况</span>
                <span className="rss-sub">
                  在住 <b className="rss-sub-done">{occupied}</b>
                  <i className="rss-sub-divider"></i>
                  共 <b>{occupied}</b> 间
                </span>
              </div>
              <button type="button" className="rss-close rss-close-cyan" onClick={() => setOpenPanel('')}>×</button>
            </div>
            <div className="rss-body rss-body-cyan">
              {occupiedRecords.length === 0 ? (
                <div className="rss-empty">暂无在住订单</div>
              ) : occupiedRecords.slice(roomPage * 10, roomPage * 10 + 10).map((order, i) => (
                <div key={order.id} className="rss-row rss-row-cyan">
                  <span className="rss-idx rss-idx-cyan">{String(roomPage * 10 + i + 1).padStart(2, '0')}</span>
                  <span className="rss-name rss-room-num">{renderTextStr(order.roomName)}</span>
                  <span className="rss-meta">{`客户: ${renderTextStr(order.customerName)}`}</span>
                  <span className="rss-meta">{renderTextStr(order.packageName)}</span>
                  <span className="rss-status rs-mom-done">在住</span>
                </div>
              ))}
            </div>
            <div className="rss-footer rss-footer-cyan">
              <button
                type="button"
                className="rss-page-btn rss-page-btn-cyan"
                disabled={roomPage === 0}
                onClick={() => setRoomPage((p) => Math.max(0, p - 1))}
              >‹ 上一页</button>
              <span className="rss-page-ind rss-page-ind-cyan">
                第 <b>{roomPage + 1}</b> / {Math.max(1, Math.ceil(occupiedRecords.length / 10))} 页
              </span>
              <button
                type="button"
                className="rss-page-btn rss-page-btn-cyan"
                disabled={roomPage >= Math.ceil(occupiedRecords.length / 10) - 1}
                onClick={() => setRoomPage((p) => p + 1)}
              >下一页 ›</button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {openPanel === 'staff' && createPortal(
        <div className="rss-overlay" onClick={() => setOpenPanel('')}>
          <div className="rss-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rss-header">
              <div className="rss-header-left">
                <span className="rss-title">员工资料列表</span>
                <span className="rss-sub">
                  在职 <b>{staffTotal}</b>
                  <i className="rss-sub-divider"></i>
                  育婴师/月嫂 <b className="rss-sub-yuesao">{nannyCount == null ? '--' : nannyCount}</b>
                </span>
              </div>
              <button type="button" className="rss-close" onClick={() => setOpenPanel('')}>×</button>
            </div>
            <div className="rss-body">
              {agentRecords.length === 0 ? (
                <div className="rss-empty">暂无在职员工</div>
              ) : agentRecords.slice(staffPage * 10, staffPage * 10 + 10).map((agent, i) => (
                <div key={agent.id} className="rss-row">
                  <span className="rss-idx">{String(staffPage * 10 + i + 1).padStart(2, '0')}</span>
                  <span className="rss-name">{renderTextStr(agent.userName || agent.user_name || agent.name)}</span>
                  <span className="rss-meta">{renderPhoneStr(agent.phone)}</span>
                  <span className="rss-status rss-status-free">在职</span>
                </div>
              ))}
            </div>
            <div className="rss-footer">
              <button
                type="button"
                className="rss-page-btn"
                disabled={staffPage === 0}
                onClick={() => setStaffPage((p) => Math.max(0, p - 1))}
              >‹ 上一页</button>
              <span className="rss-page-ind">
                第 <b>{staffPage + 1}</b> / {Math.max(1, Math.ceil(agentRecords.length / 10))} 页
              </span>
              <button
                type="button"
                className="rss-page-btn"
                disabled={staffPage >= Math.ceil(agentRecords.length / 10) - 1}
                onClick={() => setStaffPage((p) => p + 1)}
              >下一页 ›</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default RoomStaffStats;
